import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "components/Graphs/Button";
import { Input } from "components/Graphs/Input";
import { CheckCircle, ChevronDown, Copy, Search, XCircle } from "lucide-react";

import { cx } from "components/Graphs/utils";
import { FlatCard, Spinner } from "components/UI-lib";

interface LogEntry {
  id: number;
  message: string;
}

interface LogViewerProps {
  logs: string;
  didSucceed?: boolean;
  didFail?: boolean;
  title?: string;
}

const isErrorLog = (message: string) =>
  message.toLowerCase().includes("fail") ||
  message.toLowerCase().includes("failed") ||
  message.toLowerCase().includes("error");

export function LogViewer({
  logs,
  didSucceed = false,
  didFail = false,
  title = "Build Logs",
}: LogViewerProps) {
  const [filter, setFilter] = React.useState<"all" | "errors">("all");
  const [searchTerm, setSearchTerm] = React.useState("");

  const parsedLogs = React.useMemo<LogEntry[]>(() => {
    return logs
      .split("\n")
      .filter((line) => line.trim() !== "")
      .map((line, index) => ({ id: index, message: line }));
  }, [logs]);

  const errorCount = React.useMemo(() => {
    return parsedLogs.filter((log) => isErrorLog(log.message)).length;
  }, [parsedLogs]);

  const filteredLogs = React.useMemo(() => {
    return parsedLogs.filter((log) => {
      const matchesFilter =
        filter === "all" || (filter === "errors" && isErrorLog(log.message));
      const matchesSearch =
        !searchTerm ||
        log.message.toLowerCase().includes(searchTerm.toLowerCase());
      return matchesFilter && matchesSearch;
    });
  }, [parsedLogs, filter, searchTerm]);

  const handleCopy = () => {
    navigator.clipboard.writeText(logs);
  };

  const [isOpen, setIsOpen] = React.useState(true);

  return (
    <div className="w-full">
      <FlatCard>
        <Button
          variant="ghost"
          onClick={() => setIsOpen(!isOpen)}
          className="w-full flex items-center gap-3 px-4 py-3 text-left"
          aria-expanded={isOpen}
        >
          <div className="flex items-center gap-2">
            {didSucceed ? (
              <CheckCircle className="h-5 w-5 text-highlight" />
            ) : didFail ? (
              <XCircle className="h-5 w-5 text-red-500" />
            ) : (
              <div className="flex items-center h-8 w-8 text-highlight">
                <Spinner type="primary" />
              </div>
            )}
            <span className="font-semibold text-white">{title}</span>
          </div>
          <motion.div
            className="ml-auto"
            animate={{ rotate: isOpen ? 0 : -90 }}
            transition={{ duration: 0.2 }}
          >
            <ChevronDown className="h-4 w-4 shrink-0 text-secondary" />
          </motion.div>
        </Button>
        <AnimatePresence initial={false}>
          {isOpen && (
            <motion.section
              key="content"
              initial="collapsed"
              animate="open"
              exit="collapsed"
              variants={{
                open: { opacity: 1, height: "auto" },
                collapsed: { opacity: 0, height: 0 },
              }}
              transition={{ duration: 0.3, ease: [0.04, 0.62, 0.23, 0.98] }}
              className="overflow-hidden"
            >
              <div className="border-t border-zinc-800 p-4">
                <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 mb-4">
                  <div className="flex items-center gap-2">
                    <Button
                      variant="ghost"
                      onClick={() => setFilter("all")}
                      className={cx(
                        "px-3 py-1 h-auto rounded-full",
                        filter === "all"
                          ? "border border-highlight bg-zinc-800"
                          : "bg-zinc-800"
                      )}
                    >
                      All Logs ({parsedLogs.length})
                    </Button>
                    <Button
                      variant="ghost"
                      onClick={() => setFilter("errors")}
                      className={cx(
                        "px-3 py-1 h-auto rounded-full flex items-center gap-1.5",
                        filter === "errors"
                          ? "text-red-400 border border-red-500 bg-zinc-800"
                          : "bg-zinc-800"
                      )}
                    >
                      <XCircle className="h-4 w-4" /> Errors ({errorCount})
                    </Button>
                  </div>
                  <div className="flex items-center gap-2 w-full sm:w-auto sm:ml-auto">
                    <div className="relative w-full sm:w-auto">
                      <Input
                        type="search"
                        placeholder="Find in logs"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className=""
                      />
                    </div>
                    <Button variant="ghost" onClick={handleCopy}>
                      <Copy className="h-4 w-4" />
                      <span className="sr-only">Copy logs</span>
                    </Button>
                  </div>
                </div>
                <div
                  className="border border-zinc-800 rounded-md p-4 h-96 overflow-y-auto font-mono text-sm"
                  style={{
                    scrollbarWidth: "thin",
                    scrollbarColor: "#27272a #18181b",
                  }}
                >
                  {filteredLogs.map((log) => (
                    <p
                      key={log.id}
                      className={cx(
                        "whitespace-pre-wrap",
                        isErrorLog(log.message) &&
                          "text-red-600 dark:text-red-500"
                      )}
                    >
                      {log.message}
                    </p>
                  ))}
                  {didSucceed && (
                    <div className="flex items-center gap-3 mt-4 text-nvidia-green">
                      <CheckCircle className="h-4 w-4" />
                      <p className="font-semibold">
                        Build completed successfully.
                      </p>
                    </div>
                  )}
                  {didFail && (
                    <div className="flex items-center gap-3 mt-4 text-red-500 dark:text-red-400">
                      <XCircle className="h-4 w-4" />
                      <p className="font-semibold">Build failed.</p>
                    </div>
                  )}
                </div>
              </div>
            </motion.section>
          )}
        </AnimatePresence>
      </FlatCard>
    </div>
  );
}
